// Lookups in the help center table of contents (get_toc): pure, so the
// content loader (hooks/useContentLoader.jsx) and the server render
// (./ssr.js) find an article or a category the same way.

/**
 * An article in the TOC, by slug, under `categoryPath` when given (else
 * anywhere): `{ articleMetadata, breadcrumbData }`, or null.
 */
export const findArticleInToc = (tocData, slug, categoryPath = null) => {
  if (!tocData || !tocData.content || !tocData.content.categories)
    return null

  let categories = tocData.content.categories
  let breadcrumbData = []
  let foundArticle = null

  if (categoryPath) {
    const pathParts = categoryPath.split("/")
    let currentPath = ""
    // each step named as the TOC names it (translated); the slug only when it is not there
    let level = categories

    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i]
      currentPath = i === 0 ? part : `${currentPath}/${part}`
      const node = (level || []).find((c) => c.slug === part)
      level = node?.categories
      const catLabel =
        node?.label || node?.name || part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " ")
      const previousPath = i > 0 ? pathParts.slice(0, i).join("/") : ""
      breadcrumbData.push({
        label: catLabel,
        link: `/help-center/category/${currentPath}`,
        categorySlug: part,
        parentPath: previousPath,
      })
    }

    let currentCategories = categories
    for (const part of pathParts) {
      const nextCategory = currentCategories.find((c) => c.slug === part)
      if (!nextCategory) return null

      if (nextCategory.articles) {
        foundArticle = nextCategory.articles.find((a) => a.slug === slug)
        if (foundArticle) break
      }

      if (!nextCategory.categories) break
      currentCategories = nextCategory.categories
    }

    if (!foundArticle && currentCategories) {
      for (const cat of currentCategories) {
        if (cat.articles) {
          foundArticle = cat.articles.find((a) => a.slug === slug)
          if (foundArticle) break
        }
      }
    }
  } else {
    const searchInCategories = (categories, path = []) => {
      for (const cat of categories) {
        const currentPath = [...path, cat]

        if (cat.articles) {
          const article = cat.articles.find((a) => a.slug === slug)
          if (article) {
            breadcrumbData = currentPath.map((category, index) => {
              const pathSoFar = currentPath
                .slice(0, index + 1)
                .map((c) => c.slug)
                .join("/")
              return {
                label: category.label,
                link: `/help-center/category/${pathSoFar}`,
                categorySlug: category.slug,
                parentPath:
                  index > 0
                    ? currentPath
                        .slice(0, index)
                        .map((c) => c.slug)
                        .join("/")
                    : "",
              }
            })
            return { article, breadcrumbData }
          }
        }

        if (cat.categories && cat.categories.length > 0) {
          const result = searchInCategories(cat.categories, currentPath)
          if (result) return result
        }
      }
      return null
    }

    const result = searchInCategories(categories)
    if (result) {
      foundArticle = result.article
      breadcrumbData = result.breadcrumbData
    }
  }

  if (!foundArticle) return null
  return { articleMetadata: foundArticle, breadcrumbData }
}

/**
 * A category in the TOC, by its path (else by its last slug anywhere):
 * `{ categoryContent, breadcrumbData }`, or null. categoryContent has the
 * shape the category view reads (a CMS item with `content`).
 */
export const findCategoryInToc = (tocData, categoryPath) => {
  if (!tocData || !tocData.content || !tocData.content.categories || !categoryPath)
    return null

  // Try exact path traversal from root
  const tryExactPath = (categories, parts) => {
    let currentCategories = categories
    let currentCategory = null
    let breadcrumbData = []
    let currentPath = ""

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      currentPath = i === 0 ? part : `${currentPath}/${part}`
      currentCategory = currentCategories.find((c) => c.slug === part)
      if (!currentCategory) return null

      const catLabel = currentCategory.label ||
        part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " ")
      const previousPath = i > 0 ? parts.slice(0, i).join("/") : ""

      if (i === parts.length - 1) {
        breadcrumbData.push({ label: catLabel })
      } else {
        breadcrumbData.push({
          label: catLabel,
          link: `/help-center/category/${currentPath}`,
          categorySlug: part,
          parentPath: previousPath,
        })
      }

      if (currentCategory.categories) {
        currentCategories = currentCategory.categories
      } else if (i < parts.length - 1) {
        return null
      }
    }

    if (!currentCategory) return null
    return { currentCategory, breadcrumbData }
  }

  // Recursive search by last slug segment anywhere in the tree
  const findBySlug = (categories, slug, ancestorBreadcrumbs = []) => {
    for (const cat of categories) {
      if (cat.slug === slug) {
        return {
          currentCategory: cat,
          breadcrumbData: [...ancestorBreadcrumbs, { label: cat.label || slug }],
        }
      }
      if (cat.categories?.length) {
        const found = findBySlug(cat.categories, slug, [
          ...ancestorBreadcrumbs,
          {
            label: cat.label || cat.slug,
            link: `/help-center/category/${cat.slug}`,
            categorySlug: cat.slug,
            parentPath: "",
          },
        ])
        if (found) return found
      }
    }
    return null
  }

  const pathParts = categoryPath.split("/")
  let found = tryExactPath(tocData.content.categories, pathParts)

  // Fall back to searching by the last path segment
  if (!found) {
    const targetSlug = pathParts[pathParts.length - 1]
    found = findBySlug(tocData.content.categories, targetSlug)
  }

  if (!found) return null

  const { currentCategory, breadcrumbData } = found
  const categoryContent = {
    content: {
      type: "HCCategory",
      name: currentCategory.label,
      slug: currentCategory.slug,
      categories: currentCategory.categories || [],
      articles: currentCategory.articles || [],
      notes: currentCategory.notes || [],
      icon: currentCategory.icon,
    },
    _id: currentCategory._id,
    path: categoryPath,
    breadcrumbData,
  }

  return { categoryContent, breadcrumbData }
}
